import { useEffect } from "react"
import { Navigate } from "react-router-dom"
import { useFarmStore } from "@/features/farms/stores/farmStore"
import { useAuthStore } from "@/features/auth/stores/authStore"
import { buildRoute } from "@/shared/lib/constants"
import { getSavedPondId, getSavedFarmId } from "@/shared/lib/pondIdStorage"

interface LegacyRedirectProps {
  to: "ponds" | "water-quality" | "fish-health" | "maintenance" | "feed" | "ai"
}

/**
 * Redirects from legacy routes (/ponds, /water-quality, etc.)
 * to new URL structure (/farms/:farmId/water-quality?pondId=xyz, etc.)
 *
 * Uses user-scoped localStorage keys for farm/pond selection
 */
export function LegacyRedirect({ to }: LegacyRedirectProps) {
  const { selectedFarm } = useFarmStore()
  const { user } = useAuthStore()

  // Try to get IDs from store or localStorage (user-scoped)
  const farmId = selectedFarm?.id || (user?.id ? getSavedFarmId(user.id) : null)
  const pondId = user?.id ? getSavedPondId(user.id) : null

  useEffect(() => {
    // Log redirect for debugging
    console.warn(`[LegacyRedirect] Redirecting from legacy route /${to} to new structure`)
  }, [to])

  // If no farm selected, redirect to farms page
  if (!farmId) {
    return <Navigate to={buildRoute.farms()} replace />
  }

  // Build new URL based on destination
  let newUrl: string

  switch (to) {
    case "ponds":
      newUrl = buildRoute.ponds(farmId)
      break
    case "water-quality":
      if (!pondId) {
        newUrl = buildRoute.waterQuality(farmId)
        break
      }
      newUrl = buildRoute.waterQuality(farmId, pondId)
      break
    case "fish-health":
      newUrl = buildRoute.fishHealth(farmId)
      break
    case "maintenance":
      newUrl = buildRoute.maintenance(farmId)
      break
    case "feed":
      newUrl = buildRoute.feed(farmId)
      break
    case "ai":
      newUrl = `/farms/${farmId}/ai`
      break
    default:
      newUrl = buildRoute.farms()
  }

  return <Navigate to={newUrl} replace />
}
